import { gql, TypedDocumentNode } from "@apollo/client";
import {
  UniversityStudentsQuery,
  UniversityStudentsDetailQuery,
  PATStatusExportQuery,
  StudentPATResultsQuery
} from "./types";

export const STUDENTS_QUERY: TypedDocumentNode<UniversityStudentsQuery> = gql`
  query getUniversityStudentsSimplified(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeUniversityStudentsInput
  ) {
    universityStudents(first: $first, last: $last, before: $before, after: $after, input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        id
        firstName
        lastName
        displayName
        pgaId
        email
        gender
        profilePhoto
        educationLatestPgmProgram {
          startDate
          level
          levelCode
          curriculumVersion
          completed
          programType
        }
        educationPgmProgramHistory {
          level
          levelCode
          startDate
          endDate
          completed
        }
        prospectProgram {
          id
          playingAbilityTestStatus {
            status
            completed
            expired
            startDate
            expirationDate
            submissions {
              id
              date
              result
              patType
              roundOneScore
              roundTwoScore
              roundOneId
              roundTwoId
              totalScore
              targetScore
              additionalStatus
              note
              slug
              name
              suspension {
                startDate
                endDate
              }
            }
            activeSuspension {
              startDate
              endDate
            }
          }
        }
        university {
          id
          name
        }
        universityCohort {
          id
          name
        }
        totalInternshipMonths
      }
    }
  }
`;

export const STUDENTS_DETAIL_QUERY: TypedDocumentNode<UniversityStudentsDetailQuery> = gql`
  query getUniversityStudentsDetail(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeUniversityStudentsInput
  ) {
    universityStudents(first: $first, last: $last, before: $before, after: $after, input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        id
        firstName
        lastName
        displayName
        pgaId
        email
        gender
        profilePhoto
        educationLatestPgmProgram {
          startDate
          level
          levelCode
          curriculumVersion
          completed
          programType
        }
        educationPgmProgramHistory {
          level
          levelCode
          startDate
          endDate
          curriculumVersion
          completed
          programRequirements {
            requireId
            notes
            remarks
            score
            levelCode
            completionStatus
            completionDate
            isCompleted
            isInProgress
            createDate
            requirementDefinition {
              levelCode
              courseDescription
              courseId
              description
              requireId
              requireType
            }
          }
        }
        university {
          id
          name
        }
        universityCohort {
          id
          name
        }
        membershipSince
        totalInternshipMonths
        prospectProgram {
          id
          playingAbilityTestStatus {
            status
            completed
            expired
            startDate
            expirationDate
            submissions {
              date
              result
              patType
            }
            activeSuspension {
              startDate
              endDate
            }
          }
        }
      }
    }
  }
`;

export const UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY = gql`
  query getUniversityRosterRowsExists($universityId: ID!, $rosterStatus: String) {
    university(id: $universityId) {
      id
      universityRosterRows(first: 1, rosterStatus: $rosterStatus) {
        totalCount
        edges {
          node {
            roster {
              id
            }
          }
        }
        __typename
      }
      __typename
    }
  }
`;

/** Keep Eligible for Enrollment queries scoped to pending roster rows. */
export const ELIGIBLE_FOR_ENROLLMENT_ROSTER_STATUS = "eligible_for_enrollment";

/** GraphQL `universityRosterRows(rosterStatus:)` value for submitted, not-yet-finalized rosters. */
export const PENDING_REVIEW_ROSTER_STATUS = "pending";

export const STUDENTS_PAT_STATUS_EXPORT_QUERY: TypedDocumentNode<PATStatusExportQuery> = gql`
  query getUniversityStudentsPATStatusExport($input: BackofficeUniversityStudentsInput, $first: Int, $after: String) {
    universityStudents(first: $first, after: $after, input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        id
        firstName
        lastName
        displayName
        pgaId
        email
        prospectProgram {
          id
          playingAbilityTestStatus {
            startDate
            expirationDate
            status
            completed
            expired
            submissions {
              id
              patType
              date
              roundOneScore
              roundTwoScore
              roundOneId
              roundTwoId
              totalScore
              targetScore
              result
              additionalStatus
              note
              slug
              name
              suspension {
                startDate
                endDate
              }
            }
            activeSuspension {
              startDate
              endDate
            }
          }
        }
      }
    }
  }
`;

export const STUDENT_PAT_RESULTS_QUERY: TypedDocumentNode<StudentPATResultsQuery> = gql`
  query getStudentPATResults($input: BackofficeUniversityStudentsInput) {
    universityStudents(first: 1, input: $input) {
      nodes {
        id
        pgaId
        prospectProgram {
          id
          playingAbilityTestStatus {
            status
            completed
            expired
            startDate
            expirationDate
            submissions {
              id
              patType
              date
              name
              roundOneScore
              roundTwoScore
              roundOneId
              roundTwoId
              totalScore
              targetScore
              result
              additionalStatus
              note
              slug
              document {
                id
                url
              }
              suspension {
                startDate
                endDate
              }
            }
            activeSuspension {
              startDate
              endDate
            }
          }
        }
      }
    }
  }
`;
